import React, { useEffect, useState } from "react";

// Components
import { COLOR_APP } from "../../constants";
import {
  CREATE_BRAND,
  DELETE_BRAND,
  UPDATE_BRAND,
} from "../../graphql/brand/mutation";
import { GET_BRANDS } from "../../graphql/brand/query";
import LoadingSpinner from "../../components/LoadingSpinner";
import ModalConfirmComponent from "../../components/ModalConfirmComponent";
import usePermission from "../../helpers/usePermission";
import { useQueryCategoryData } from "../../services/categoryService/queries";

// Libraries
import { Button, Table, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useLazyQuery, useMutation } from "@apollo/client";
import moment from "moment";
import _ from "lodash";

// Icons
import { BiPurchaseTag } from "react-icons/bi";
import { IoMdAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";

//* ໜ້າຈັດການແບຣນສິນຄ້າ — ໂຄງສ້າງຄືກັບ CategoryLists, ຟັງຊັ່ນຄືກັບໜ້າແບຣນຂອງ POS
//* ແບຣນໃຊ້ຂໍ້ມູນຊຸດດຽວກັນກັບ POS → ສ້າງຢູ່ນີ້ ຫຼື POS ກໍເຫັນຄືກັນ
const BRAND_LIMIT = 200;

// error ຈາກ backend ມາເປັນ "Error: BRAND_ALREADY_EXIST" → ແປເປັນຂໍ້ຄວາມ
const toErrorText = (error) =>
  String(error?.message || "").includes("BRAND_ALREADY_EXIST")
    ? "ແບຣນນີ້ມີຢູ່ແລ້ວໃນປະເພດນີ້"
    : "ບັນທຶກບໍ່ສຳເລັດ ກະລຸນາລອງໃໝ່";

export default function BrandLists({ shopId }) {
  //* ສິດຍ່ອຍຂອງ "ຈັດການສິນຄ້າ" — ບໍ່ມີສິດ → ຊ່ອນປຸ່ມ
  const canCreate = usePermission("PRODUCTS_CREATE");
  const canUpdate = usePermission("PRODUCTS_UPDATE");
  const canDelete = usePermission("PRODUCTS_DELETE");
  const [isAddModal, setIsAddModal] = useState(false);
  const [isDelModal, setIsDelModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [searchCategory, setSearchCategory] = useState("ALL");
  const [brandName, setBrandName] = useState("");
  const [brandCategory, setBrandCategory] = useState("");
  const [prepareId, setPrepareId] = useState();
  const [errorText, setErrorText] = useState("");

  const { queryCategoryDatas, loadCategoryData } = useQueryCategoryData();
  const categoryDatas = loadCategoryData?.categories?.data || [];

  // GraphQL Clients
  const [getBrands, { data: loadBrandData, loading: loadingBrands }] =
    useLazyQuery(GET_BRANDS, { fetchPolicy: "cache-and-network" });
  const [createBrand, { loading: loadingCreate }] = useMutation(CREATE_BRAND);
  const [updateBrand, { loading: loadingUpdate }] = useMutation(UPDATE_BRAND);
  const [deleteBrand, { loading: loadingDelete }] = useMutation(DELETE_BRAND);

  const brandDatas = loadBrandData?.brands?.data || [];
  const brandTotal = loadBrandData?.brands?.total ?? 0;

  // useEffect Hooks
  useEffect(() => {
    queryCategoryDatas({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBrandData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchCategory, shopId]);

  // Handle fetch data
  const fetchBrandData = () => {
    let _where = { shop: shopId, isDeleted: false };
    if (!_.isEmpty(search)) _where = { ..._where, name: search };
    if (searchCategory !== "ALL")
      _where = { ..._where, category: searchCategory };
    getBrands({
      variables: {
        where: _where,
        orderBy: "createdAt_DESC",
        limit: BRAND_LIMIT,
      },
    });
  };

  const onCloseModal = () => {
    setBrandName("");
    setBrandCategory("");
    setErrorText("");
    setIsAddModal(false);
    setValidated(false);
    setIsDelModal(false);
    setEditMode(false);
  };

  // create brand data
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (loadingCreate) return;
    if (form.checkValidity() !== true) {
      setValidated(true);
      return;
    }
    try {
      setErrorText("");
      await createBrand({
        variables: {
          data: {
            name: brandName,
            category: brandCategory || null,
            shop: shopId,
          },
        },
      });
      fetchBrandData();
      onCloseModal();
    } catch (error) {
      setErrorText(toErrorText(error));
    }
  };

  // update brand
  const onUpdateBrand = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (loadingUpdate) return;
    if (form.checkValidity() !== true) {
      setValidated(true);
      return;
    }
    try {
      setErrorText("");
      await updateBrand({
        variables: {
          where: { id: prepareId },
          data: { name: brandName, category: brandCategory || null },
        },
      });
      fetchBrandData();
      onCloseModal();
    } catch (error) {
      setErrorText(toErrorText(error));
    }
  };

  // confirm delete brand data
  const onDeleteBrand = async () => {
    try {
      if (loadingDelete) return;
      await deleteBrand({ variables: { where: { id: prepareId } } });
      onCloseModal();
      fetchBrandData();
    } catch (error) {
      console.error(error);
    }
  };

  // edit brand
  const handleEdit = (data) => {
    setEditMode(true);
    setIsAddModal(true);
    setBrandName(data?.name ?? "");
    setBrandCategory(data?.category?.id ?? "");
    setPrepareId(data?.id);
  };

  // prepare confirm modal deleted
  const handleDelete = (id) => {
    setIsDelModal(true);
    setPrepareId(id);
  };

  return (
    <>
      <div className="d-flex flex-column p-4 w-100">
        <div className="d-flex gap-2 justify-content-start align-items-center">
          <BiPurchaseTag style={{ fontSize: 27 }} />
          <h5>ລາຍການແບຣນສິນຄ້າ [{brandTotal}]</h5>
        </div>
        <br />
        <div className="d-flex w-100 gap-4 justify-content-between align-items-center">
          <Form.Group className="mb-3 w-100" controlId="brandSearch">
            <Form.Label>ຄົ້ນຫາ</Form.Label>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              type="text"
              placeholder="ປ້ອນຊື່ແບຣນ..."
            />
          </Form.Group>
          <Form.Group className="mb-3 w-100" controlId="brandSearchCategory">
            <Form.Label>ປະເພດ</Form.Label>
            <Form.Select
              value={searchCategory}
              onChange={(e) => setSearchCategory(e?.target?.value)}
            >
              <option value="ALL">ປະເພດທັງໝົດ</option>
              {categoryDatas.map((item) => (
                <option key={item?.id} value={item?.id}>
                  {item?.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {canCreate && (
          <Button
            style={{
              background: COLOR_APP,
              marginTop: 15,
              color: "#fff",
              minWidth: 200,
            }}
            onClick={() => setIsAddModal(true)}
          >
            <IoMdAdd />
            ເພິ່ມ
          </Button>
          )}
        </div>
        <div className="py-2">
          {loadingBrands && !loadBrandData ? (
            <LoadingSpinner />
          ) : brandDatas.length === 0 ? (
            <div className="text-center text-muted py-5">
              ຍັງບໍ່ມີແບຣນ ກະລຸນາເພີ່ມແບຣນກ່ອນ
            </div>
          ) : (
            <Table striped bordered>
              <thead>
                <tr>
                  <th style={{ border: "none" }}>ລຳດັບ</th>
                  <th style={{ border: "none" }}>ຊື່ແບຣນ</th>
                  <th style={{ border: "none" }}>ປະເພດທີ່ລີ້ງ</th>
                  <th style={{ border: "none" }}>ວັນທີ,ເດືອນ,ປີ</th>
                  <th style={{ border: "none", textAlign: "right" }}>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {brandDatas.map((item, index) => (
                  <tr key={item?.id || index}>
                    <td>{index + 1}</td>
                    <td>{item?.name ?? "-"}</td>
                    <td>{item?.category?.name ?? "-"}</td>
                    <td>{moment(item?.createdAt).format("DD/MM/YYYY")}</td>
                    <td style={{ width: 200, textAlign: "right" }}>
                      {canUpdate && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit />
                        ແກ້ໄຂ
                      </Button>
                      )}
                      &nbsp;
                      {canDelete && (
                      <Button
                        variant="outline-danger"
                        onClick={() => handleDelete(item?.id)}
                      >
                        <MdDelete />
                        ລົບ
                      </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal centered show={isAddModal} onHide={onCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "ແກ້ໄຂແບຣນສິນຄ້າ" : "ເພິ່ມແບຣນສິນຄ້າ"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            noValidate
            validated={validated}
            onSubmit={editMode ? onUpdateBrand : handleSubmit}
          >
            <Row className="mb-3 w-100">
              <Form.Group controlId="brandCategory" className="w-100 mb-3">
                <Form.Label>ເລືອກເຊື່ອມກັບປະເພດ</Form.Label>
                <Form.Select
                  value={brandCategory}
                  onChange={(e) => setBrandCategory(e?.target?.value)}
                >
                  <option value="">ບໍ່ເຊື່ອມປະເພດ</option>
                  {categoryDatas.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {item?.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="brandName" className="w-100">
                <Form.Label>ຊື່ແບຣນ</Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="ປ້ອນຊື່ແບຣນ"
                  className="w-100"
                  value={brandName}
                  onChange={(e) => setBrandName(e?.target?.value)}
                />
                <Form.Control.Feedback type="invalid">
                  ກາລຸນາປ້ອນຂໍ້ມູນກ່ອນ!
                </Form.Control.Feedback>
              </Form.Group>
              {errorText && (
                <div className="text-danger mt-2">{errorText}</div>
              )}
            </Row>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={onCloseModal}
              >
                ຍົກເລິກ
              </Button>
              <Button
                type="submit"
                style={{ background: COLOR_APP, color: "#fff" }}
              >
                {(editMode ? loadingUpdate : loadingCreate) && <Spinner />}{" "}
                {editMode ? "ແກ້ໄຂ" : "ບັນທຶກ"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* confirm deleted brand */}
      <ModalConfirmComponent
        showConfirmModal={isDelModal}
        handleCancel={() => setIsDelModal(false)}
        handleConfirm={onDeleteBrand}
        loadingConfirm={loadingDelete}
        title="ແຈ້ງເຕືອນ"
        text={"ທ່ານຕ້ອງການລົບແບຣນນີ້ແທ້ບໍ່?"}
      />
    </>
  );
}
